import React, { Component } from 'react';
import { debounce } from 'throttle-debounce';
import type { TChangeEventHandler, TFocusEvent } from '@alfa-bank/site-input/models';

import type {
    IState,
    IController,
    TDaDataNamePart,
    TOneFieldFio,
    INameParts,
    IQueryConfigVars,
    THandleChangeNamePart,
    TCurrentSuggestion,
} from './models';
import {
    DA_DATA_FIRST_NAME,
    DA_DATA_LAST_NAME,
    DA_DATA_PATRONYMIC,
    DEFAULT_REQUEST_DELAY,
    daDataNamesToFrontNames,
    DEFAULT_REQUEST_MIN_LENGTH,
} from './config';
import { DaDataService } from './dadata-service';
import { OneFieldController } from './components/one-field-controller';
import type { IFioDropdown } from './components/field/models';
import {
    defaultCallback,
    getFioGender,
    getFioParams,
    getFioQc,
    getPartialFioGender,
    getSelectOptions,
    splitFIO,
    daDataFunctions,
    updateAbortControllers,
    mapResultToState,
    getDefaultSuggestion,
    filterByValue,
    getCurrentSuggestionValue,
} from './helpers';
import type { ISuggestion, ISuggestionRequest } from './dadata-service/models';
import { FioCache } from './fio-cache';

export class OneFieldFio extends Component<TOneFieldFio, IState> {
    daDataService = new DaDataService();

    fetchNamePart = daDataFunctions(this.daDataService);

    controllers: IController = {
        [DA_DATA_FIRST_NAME]: [],
        [DA_DATA_LAST_NAME]: [],
        [DA_DATA_PATRONYMIC]: [],
    };

    handleDebounceLastName: THandleChangeNamePart;

    handleDebounceFirstName: THandleChangeNamePart;

    handleDebouncePatronymic: THandleChangeNamePart;

    cache: FioCache;

    constructor(props: TOneFieldFio) {
        super(props);

        const { value = '', requestDelay = DEFAULT_REQUEST_DELAY } = props;
        const { firstName, lastName, patronymic } = splitFIO(String(value));

        // eslint-disable-next-line react/state-in-constructor
        this.state = {
            lastName,
            firstName,
            patronymic,
        };

        this.cache = new FioCache();
        this.handleChangeLastName = this.handleChangeLastName.bind(this);
        this.handleChangeFirstName = this.handleChangeFirstName.bind(this);
        this.handleChangePatronymic = this.handleChangePatronymic.bind(this);
        this.handleDebounceLastName = debounce(requestDelay, this.handleChangeNamePart);
        this.handleDebounceFirstName = debounce(requestDelay, this.handleChangeNamePart);
        this.handleDebouncePatronymic = debounce(requestDelay, this.handleChangeNamePart);
    }

    handleChangeLastName = (lastName: string) => {
        this.handleDebounceLastName(DA_DATA_LAST_NAME, lastName);
    };

    handleChangeFirstName = (firstName: string) => {
        this.handleDebounceFirstName(DA_DATA_FIRST_NAME, firstName);
    };

    handleChangePatronymic = (patronymic: string) => {
        this.handleDebouncePatronymic(DA_DATA_PATRONYMIC, patronymic);
    };

    async handleChangeNamePart(namePart: TDaDataNamePart, value: string) {
        const { onChangeNamePart = defaultCallback } = this.props;

        /** добавляем контроллер для отмены просроченного запроса */
        const controller = new AbortController();

        this.controllers[namePart].push({ controller, value });

        /** проверяем, не сохранено ли у нас уже в кеше это значение */
        const result = await this.getSuggestions(namePart, value, controller);

        /** абортим предыдущие запросы, по этой части фио, если они еще не разрезолвились, и обновляем this.controllers */
        updateAbortControllers(this.controllers[namePart], value);
        /** если запрос не был отменен - записываем новое состояние */
        if (!controller.signal.aborted) {
            const namePartState = mapResultToState({ namePart, value, result });

            this.setState(prevState => ({
                ...prevState,
                ...namePartState,
            }));
        }

        const nameParts = this.getActualNameParts(namePart, value);

        onChangeNamePart(nameParts);
    }

    getSuggestions(
        namePart: TDaDataNamePart,
        value: string,
        controller: AbortController
    ): Promise<ISuggestion[]> {
        /** чтобы при стирании не отправлялись пустые запросы в дадату */
        if (value === '') {
            return Promise.resolve([]);
        }

        /** если ответ уже есть в кеше, отдаем оттуда */
        const cacheSuggestions = this.cache.get(namePart, value);
        if (cacheSuggestions) {
            return Promise.resolve(cacheSuggestions);
        }

        const { minRequestLength = DEFAULT_REQUEST_MIN_LENGTH } = this.props;

        /** делаем запрос по соответствующей части имени */
        const queryConfig = this.getQueryConfig({
            namePart,
            value,
            controller,
        });

        let result: Promise<ISuggestion[]> = getDefaultSuggestion(namePart, value);
        if (value.length >= minRequestLength) {
            result = this.fetchNamePart[namePart](queryConfig);
        }

        this.cache.set({ namePart, value, data: result });

        return result;
    }

    getQueryConfig({ controller, namePart, value }: IQueryConfigVars): ISuggestionRequest {
        const { firstName } = this.state;

        /** находим suggestions соответствующие ФИО */
        const fioParams = getFioParams(this.state);
        /** определяем пол на текущий момент */
        const isLastNameFinished = !!firstName || (namePart === DA_DATA_FIRST_NAME && !!value);
        const gender = getPartialFioGender(fioParams, isLastNameFinished);

        return { value, gender, controller };
    }

    getActualNameParts(namePart: TDaDataNamePart, value: string): INameParts {
        const { firstName, lastName, patronymic } = this.state;

        const frontNamePart = daDataNamesToFrontNames[namePart];

        return {
            lastName,
            firstName,
            patronymic,
            [frontNamePart]: value,
        };
    }

    handleBlurField = (obj: TFocusEvent) => {
        const { firstName, lastName, patronymic } = this.state;
        const { onBlur = defaultCallback, onComplete = defaultCallback } = this.props;
        const { value = '' } = obj;

        const { restWords } = splitFIO(String(value));
        /** находим suggestions соответствующие ФИО */
        const fioParams = getFioParams(this.state);

        /** на их основе вычисляем пол и степень достоверности */
        const gender = getFioGender(fioParams);
        const qc = getFioQc(fioParams, restWords);

        const completeParams = {
            lastName,
            firstName,
            patronymic,
            restWords,
            gender,
            qc,
        };

        onBlur(obj);
        onComplete(completeParams, obj);
        this.clearNotChosenSuggestions();
    };

    clearNotChosenSuggestions = () => {
        const { currentSuggestions } = this.state;

        if (!currentSuggestions) {
            return;
        }

        const suggestions = this.state[currentSuggestions];

        if (!suggestions || !suggestions.length) {
            return;
        }

        this.setState(prevState => {
            const { firstName, lastName, patronymic } = prevState;
            const nameParts = { firstName, lastName, patronymic };
            const currentValue = getCurrentSuggestionValue(currentSuggestions, nameParts);
            const filteredSuggestions = filterByValue(suggestions, currentValue);
            const current = { [currentSuggestions as TCurrentSuggestion]: filteredSuggestions };

            return {
                ...prevState,
                ...current,
            };
        });
    };

    handleSelect = (obj: IFioDropdown) => {
        const { onSelect = defaultCallback } = this.props;

        this.updateNamePartsByNewValue(obj.value);

        onSelect(obj);
    };

    handleChange: TChangeEventHandler = obj => {
        const { onChange = defaultCallback } = this.props;

        this.updateNamePartsByNewValue(obj.value.toString());

        onChange(obj);
    };

    updateNamePartsByNewValue = (value: string) => {
        const { firstName, lastName, patronymic } = splitFIO(value);

        this.setState(
            {
                lastName,
                firstName,
                patronymic,
            },
            () => this.clearNotChosenSuggestions()
        );
    };

    render() {
        /** Следующие пропсы используются только внутри этого компонента и их не нужно прокидывать дальше:
         * - requestDelay
         * - onChangeNamePart
         * - minRequestLength
         *
         * Если их передать дальше, то из-за ...spread и ...rest внутри дочерних элементов они будут попадать в инпуты как html аттрибуты
         * */
        const {
            hideSuggestions,
            minRequestLength,
            onChangeNamePart,
            onComplete,
            requestDelay,
            validate,
            value = '',
            ...restProps
        } = this.props;
        const options = hideSuggestions ? [] : getSelectOptions(this.state);

        return (
            <OneFieldController
                {...restProps}
                fetchFirstName={this.handleChangeFirstName}
                fetchLastName={this.handleChangeLastName}
                fetchPatronymic={this.handleChangePatronymic}
                onBlur={this.handleBlurField}
                onChange={this.handleChange}
                onSelect={this.handleSelect}
                options={options}
                validate={validate}
                value={String(value)}
            />
        );
    }
}
